import { useEffect, useState } from "react";
import useApi from "../../../../../hooks/use-api";
import { Controller, useForm } from "react-hook-form";
import styles from '../../../../../styles/transitionplan.module.css';
import { Button, Spinner } from "react-bootstrap";
import RHFButtonListSelect from "../../../../../rhf/rhfbuttonlistselect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSave } from "@fortawesome/free-solid-svg-icons";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import FormInputValidationMessage from "../../../../../components/forminputvalidationmessage";
import { EmptyStringToNull } from "../../../../../utilities/rhfdatanormalizationfunctions";

const NeedsAddEdit = ({ApplicationId,NeedId = null,ReturnFunction,RefreshFunction}) => {
    const [needTypes,setNeedTypes] = useState([]);
    const [needCategories,setNeedCategories] = useState([]);
    const [isLoading,SetisLoading] = useState(true);
    const [feedback, setFeedback] = useState(null);

    const initialValues = {
        needtypeid: null,
        needcategoryid: null,
        needtitle: '',
        needcomment: ''
    }

    const validationSchema = Yup.object().shape({
        needtypeid: Yup.string().required('*Type is Required'),
        needcategoryid: Yup.string().required('*Category is Required')
    });

    const {control,register,handleSubmit,reset,formState: {errors,isDirty,isSubmitting}} = useForm({defaultValues: initialValues,resolver: yupResolver(validationSchema)});

    const {getRequest,postRequest,putRequest} = useApi();

    const prepareDataForSend = (data) => {
            const transformedObj = {};
            for (const key in data) {
                if (data.hasOwnProperty(key)) {
                    transformedObj[key] = EmptyStringToNull(data[key]);
                }
            }
            return transformedObj;
            
    }

    const _onSubmit = async(items) => {
        setFeedback(null);
        let senditem = {
            NeedTypeID : items.needtypeid,
            NeedCategoryID : items.needcategoryid,
            NeedTitle : items.needtitle,
            NeedComment : items.needcomment
        }

        const sendItem = prepareDataForSend(senditem);

        if(NeedId){
            const [data,status] = await putRequest('UpdateNeed',{...sendItem,ApplicationID : ApplicationId,NeedID: NeedId});

            if(status === 200){
                setFeedback({ type: "success", message: "The Need was successfully updated." });
                const {id,goals,...dataValues} =  data;

                RefreshFunction({ preserveEditorViewport: true });
                reset({
                    needtypeid: dataValues.needtypeid,
                    needcategoryid: dataValues.needcategoryid,
                    needtitle: dataValues.needtitle === null ? '' : dataValues.needtitle,
                    needcomment: dataValues.needcomment === null ? '' : dataValues.needcomment
                });
                
            }
            else{
                setFeedback({ type: "error", message: "An error occurred when updating the Need." });
            }
        }
        else{
            const [data,status] = await postRequest('AddNeed',{...sendItem,ApplicationID: ApplicationId});

            if(status === 201){
                setFeedback({ type: "success", message: "The Need was successfully added." });
        
                RefreshFunction({ preserveEditorViewport: true });
                reset(initialValues);
            }
            else{
                setFeedback({ type: "error", message: "An error occurred when adding the Need." });
            }
        }
    }

    const get = async () => {
        SetisLoading(true);
        const [typesData,needData] = await Promise.all([
            getRequest('NeedTypesCat'),
            getRequest('Need',{ApplicationID : ApplicationId,NeedID : NeedId})
        ]);

        SetisLoading(false);

        const [extractedTypeData,typeStatus] = typesData;
        const [extractedNeedData,needStatus] = needData;

        if(typeStatus === 200 && needStatus === 200){
            setNeedTypes(extractedTypeData.types);
            setNeedCategories(extractedTypeData.categories);

            const{id,goals,...dataValues} = extractedNeedData;

            const resetVals ={
                ...dataValues,
                needtitle: dataValues.needtitle === null ? '' : dataValues.needtitle,
                needcomment: dataValues.needcomment === null ? '' : dataValues.needcomment
            }

            reset(resetVals);
        }
        else{
            setNeedTypes([]);
            setNeedCategories([]);
        }
    }

    const getTypes =  async() => {
        SetisLoading(true);
        const [data,status] = await getRequest('NeedTypesCat',{timestamp: Date.now()});
        SetisLoading(false);

        if(status === 200){
            setNeedCategories(data.categories);
            setNeedTypes(data.types);
        }
        else{
            //Handle error
            setNeedCategories([]);
            setNeedTypes([]);
        }
    }

    useEffect(() => {
        if(NeedId){
            get();
        }
        else{
            getTypes();
        }
    },[NeedId])

    if(isLoading){
        return(
            <div className={styles.statusMessage} role="status" aria-live="polite">Loading need form...</div>
        )
    }

    return(
        <form onSubmit={handleSubmit(_onSubmit)}>
            <div className={`clientCard ${styles.editorEmbeddedCard}`}>
                <div className={`${styles.needBody} ${styles.editorEmbeddedBody}`}>
                    <Controller
                        name="needtypeid"
                        control={control}
                        render={({field}) => (
                                <RHFButtonListSelect
                                    label="Need Type"
                                    options={needTypes}
                                    {...field}
                                />
                            )}
                        />
                        <FormInputValidationMessage error={errors.needtypeid} />
                        <Controller
                            name="needcategoryid"
                            control={control}
                            render={({field}) => (
                                    <RHFButtonListSelect
                                        label="Need Category"
                                        options={needCategories}
                                        {...field}
                                    />
                                )}
                        />
                        <FormInputValidationMessage error={errors.needcategoryid} />
                        <div>
                            <label htmlFor="needtitle">Need</label>
                            <input type="text" id="needtitle" style={{width: '100%'}} {...register('needtitle')} />
                        </div>
                        <div>
                            <label htmlFor="needcomment">Comment</label>
                            <textarea id="needcomment" rows={4} style={{width: '100%'}} {...register('needcomment')} />
                        </div>
                        {feedback && (
                            <div className={`${styles.inlineFeedback} ${feedback.type === "error" ? styles.inlineFeedbackError : styles.inlineFeedbackSuccess}`} role="status" aria-live="polite">
                                {feedback.message}
                            </div>
                        )}
                        <div className="d-flex justify-content-end">
                            <Button type="submit" variant={NeedId !== null ? 'primary' : 'success'} disabled={isLoading || !isDirty || isSubmitting}>
                                {
                                    isSubmitting
                                    ?
                                        <Spinner animation='border' size='sm' aria-hidden="true" />
                                    :
                                        NeedId
                                        ?
                                            <>
                                                <FontAwesomeIcon icon={faSave} size="xl" className="me-1" />
                                                Save
                                            </>
                                        :
                                            <>
                                                <FontAwesomeIcon icon={faPlus} size="xl" className="me-1" />
                                                Add Need
                                            </>
                                }
                            </Button>
                        </div>
                </div>
            </div>
        </form>
    )
}

export default NeedsAddEdit;
